/**
 * Display Name Field Component
 * Simple input field for display name with basic formatting
 */

import { forwardRef } from 'react';

export interface DisplayNameFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  label?: string;
  helpText?: string;
}

export const DisplayNameField = forwardRef<
  HTMLInputElement,
  DisplayNameFieldProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = 'Enter your display name',
      disabled = false,
      required = false,
      id = 'display_name',
      className = '',
      label = 'Display Name',
      helpText = 'This is how your name will appear to other users',
      ...props
    },
    ref
  ) => {
    const getInputClasses = () => {
      const baseClasses = 'input transition-colors duration-200';
      return `${baseClasses} ${className}`;
    };

    return (
      <div className="form-field">
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        <div className="relative">
          <input
            {...props}
            ref={ref}
            type="text"
            id={id}
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={getInputClasses()}
            aria-describedby={helpText ? `${id}-message` : undefined}
          />
        </div>

        {/* Help text */}
        {helpText && (
          <div
            id={`${id}-message`}
            className="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            {helpText}
          </div>
        )}
      </div>
    );
  }
);

DisplayNameField.displayName = 'DisplayNameField';
